/** Flatten sidebar trees and rank page matches for the header search. */

const ALIASES = {
  '/': ['home', 'overview'],
  '/purchase-order': ['po'],
  '/purchase-order/po-documents': ['po documents', 'advance license'],
  '/purchase-entry': ['grn', 'goods receipt'],
  '/purchase-entry/grn-number': ['grn'],
  '/purchase-entry/lot-number-list': ['lot'],
  '/inventory/common/company-name': ['mill', 'mill name'],
  '/inventory/common/sub-group': ['gsm'],
  '/inventory/pantone-number': ['pantone', 'pms', 'ink'],
  '/user-role': ['user', 'role', 'permission', 'users'],
  '/processing-unit-list': ['machine', 'job work'],
  '/vendor-management': ['supplier', 'vendors'],
  '/account': ['billing', 'reports', 'finance', 'accounts'],
  '/account/insights': ['ai insights', 'insights', 'ai'],
  '/account/customers': ['billing customer', 'account customer'],
  '/account/items': ['billing item', 'account item'],
  '/account/invoice': ['billing invoice', 'account invoice'],
  '/account/settings': ['gst master', 'payment terms', 'account settings', 'gst'],
  '/documents': ['docs', 'light bill', 'pf', 'tally'],
};

/** Ctrl/Cmd+K or Ctrl/Cmd+F — skip Shift so Ctrl+Shift+F stays with the browser. */
export function isSearchHotkey(e) {
  if (!e || e.altKey || e.shiftKey) return false;
  if (!(e.metaKey || e.ctrlKey)) return false;
  const k = String(e.key || '').toLowerCase();
  return k === 'k' || k === 'f';
}

const STOP = new Set(['pvt', 'ltd', 'llc', 'inc', 'limited', 'private', 'company', 'co', 'the', 'and']);

const tokensOf = (s) => String(s || '').toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);

function tokenScore(token, field) {
  if (!token || !field) return 0;
  if (field === token) return 4;
  const words = tokensOf(field);
  if (words.includes(token)) return 3;
  if (token.length >= 3 && words.some((w) => w.startsWith(token))) return 2;
  if (token.length >= 4 && field.includes(token)) return 1;
  return 0;
}

/** Rank how well `q` hits the given fields. 0 = no match. */
export function scoreQuery(q, fields) {
  const raw = tokensOf(q);
  if (!raw.length) return 0;
  const tokens = raw.filter((t) => !STOP.has(t));
  if (!tokens.length) return 0;
  const vals = (fields || []).filter(Boolean).map((f) => String(f).toLowerCase());
  if (!vals.length) return 0;
  let total = 0;
  for (const t of tokens) {
    let best = 0;
    for (const f of vals) best = Math.max(best, tokenScore(t, f));
    if (!best) return 0;
    total += best;
  }
  return total;
}

export function matchesQuery(q, ...parts) {
  return scoreQuery(q, parts) > 0;
}

export function topHits(rows, q, pick, limit = 4) {
  return (rows || [])
    .map((r) => ({ r, score: scoreQuery(q, pick(r)) }))
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((x) => x.r);
}

export function flattenMenu(tabs, parent = '') {
  const out = [];
  (tabs || []).forEach((tab) => {
    if (!tab || tab.link) return;
    const crumb = parent ? `${parent} / ${tab.name}` : (tab.section || tab.name);
    const isFolder = tab.name === 'Common';
    if (tab.path && !isFolder) {
      const aliases = ALIASES[tab.path] || [];
      out.push({
        id: `page:${tab.path}`,
        group: 'Pages',
        title: tab.name,
        subtitle: parent || tab.section || 'Page',
        path: tab.path,
        keywords: [tab.name, crumb, tab.section, ...aliases].filter(Boolean).join(' ').toLowerCase(),
      });
    }
    if (tab.subData) out.push(...flattenMenu(tab.subData, isFolder ? parent || 'Inventory' : tab.name));
  });
  return out;
}

export function matchPages(query, pages, limit = 8) {
  const q = (query || '').trim().toLowerCase();
  if (!q) return (pages || []).slice(0, limit);
  return (pages || [])
    .map((p) => ({ ...p, score: scoreQuery(q, [p.title, p.keywords]) }))
    .filter((p) => p.score > 0)
    .sort((a, b) => b.score - a.score || a.title.localeCompare(b.title))
    .slice(0, limit);
}

export function listRows(res) {
  const rows = res?.payload?.data?.response;
  return Array.isArray(rows) ? rows : [];
}
